/**
 * The design system's `.seg`, as a radio group.
 *
 * Radios rather than buttons because it is a choice among known options, which is what a radio
 * group is: arrow keys move between them, a screen reader announces "1 of 3", and the label
 * association comes free. Two styled buttons would have to rebuild all three.
 *
 * An option may be disabled on its own. That is not decoration — a shell's `byo` template is a
 * real, current value that this form may not *set*, so it has to be shown as chosen and refused
 * at the same time. `title` carries the reason; a disabled option with no explanation is a dead
 * end.
 */
export interface SegmentedOption {
  value: string;
  label: string;
  disabled?: boolean;
  reason?: string | undefined;
}

export function Segmented({
  name, value, options, disabled = false, onChange,
}: {
  name: string;
  value: string;
  options: readonly SegmentedOption[];
  disabled?: boolean;
  onChange(value: string): void;
}) {
  return (
    <div className="border-input inline-flex w-fit flex-wrap overflow-hidden rounded-[var(--radius)] border">
      {options.map((option, index) => {
        const off = disabled || Boolean(option.disabled);
        return (
          <label
            key={option.value}
            htmlFor={`${name}-${option.value}`}
            title={option.reason}
            className={[
              'px-3 py-1.5 text-[13px] select-none',
              index > 0 ? 'border-input border-l' : '',
              option.value === value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
              off ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              'has-[input:focus-visible]:outline-ring has-[input:focus-visible]:-outline-offset-2 has-[input:focus-visible]:outline-2',
            ].join(' ')}
          >
            <input
              type="radio"
              id={`${name}-${option.value}`}
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={off}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  );
}
